import noop from 'lodash/noop'
import { type FC, useEffect, useRef, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'

import { IS_DEVELOPMENT } from '../../utils/isDevelopment'
import * as Styled from './styles'

interface YousignData {
  type: string
  event: string
  // eslint-disable-next-line @typescript-eslint/naming-convention
  signer_id: string
  // eslint-disable-next-line @typescript-eslint/naming-convention
  signature_request_id: string
}

interface YousignAnswer {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  field_id: string
  // eslint-disable-next-line @typescript-eslint/naming-convention
  field_type: 'text' | 'checkbox' | 'radio_group'
}

interface YousignDataWithAnswers extends YousignData {
  answers: YousignAnswer[]
}

interface YousignFrameProps {
  height?: string
  signatureLink: string
  onStarted?: (data: YousignData & { event: 'started' }) => void
  onSuccess?: (data: YousignDataWithAnswers & { event: 'success' }) => void
  onError?: (data: YousignDataWithAnswers & { event: 'error' }) => void
  onPing?: (data: YousignData & { event: 'ping' }) => void
  onDeclined?: (data: YousignData & { event: 'declined' }) => void
  onLeaveFullscreen?: () => void
}

// https://developers.yousign.com/docs/iframe-advanced
export const YousignFrame: FC<YousignFrameProps> = ({
  height,
  onDeclined = noop,
  onError = noop,
  onPing = noop,
  onStarted = noop,
  onSuccess = noop,
  signatureLink,
}) => {
  const isMounted = useRef(false)
  const [iframeContainerId] = useState(`yousign-iframe-${uuidv4()}`)
  const [finalSignatureLink, setFinalSignatureLink] = useState(signatureLink)

  const onDeclinedRef = useRef(onDeclined)
  const onErrorRef = useRef(onError)
  const onPingRef = useRef(onPing)
  const onStartedRef = useRef(onStarted)
  const onSuccessRef = useRef(onSuccess)

  useEffect(() => {
    isMounted.current = true
    const init = async () => {
      if (!isMounted.current || !window.Yousign) {
        return
      }

      const isSandbox = IS_DEVELOPMENT
      if (isSandbox) {
        const sLink = new URL(signatureLink)
        sLink.searchParams.append('disable_domain_validation', 'true')
        setFinalSignatureLink(sLink.href)
      }

      const yousign = new window.Yousign({
        iframeContainerId,
        isSandbox,
        signatureLink: finalSignatureLink,
      })

      // Bind all events
      yousign.onDeclined(onDeclinedRef.current)
      yousign.onError(onErrorRef.current)
      yousign.onPing(onPingRef.current)
      yousign.onStarted(onStartedRef.current)
      yousign.onSuccess(onSuccessRef.current)

      setTimeout(() => {
        Object.assign(document.getElementById('yousign-iframe')?.style ?? {}, {
          height: '100%',
          width: '100%',
        })
      }, 1000)
    }
    init()

    return () => {
      isMounted.current = false
    }
  }, [
    finalSignatureLink,
    iframeContainerId,
    onDeclinedRef,
    onErrorRef,
    onPingRef,
    onStartedRef,
    onSuccessRef,
    signatureLink,
  ])

  return (
    <Styled.YousignIframe
      height={height}
      allowFullScreen
      id={iframeContainerId}
      src={finalSignatureLink}
      title="Yousign Signature"
    />
  )
}
